import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DynamicService } from '../../../core/services/api/dynamic/dynamic.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

interface Filter {
  id: number;
  columnName: string;
  type: number;
  relatedFilter?: string;
  orderByColumn?: string;
  priority?: number;
}

interface FilterOptionsState {
  options: any[];
  page: number;
  pageSize: number;
  search: string;
  hasMore: boolean;
  loading: boolean;
  searchSubject?: Subject<string>;
}

@Component({
  selector: 'app-view-quek-filter',
  templateUrl: './view-quek-filter.component.html',
  styleUrls: ['./view-quek-filter.component.scss'],
})
export class ViewQuekFilterComponent implements OnChanges, OnDestroy {
  @Input() availableFilters: Filter[] = [];
  @Input() initFilters: Array<{ filterId: number; value: any }> = [];
  @Output() filterSubmitted = new EventEmitter<Array<{ filterId: number; value: any }>>();

  form: FormGroup;
  filterStates: { [key: number]: FilterOptionsState } = {};

  public showAfterValueChange: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dynamicService: DynamicService,
    private message: NzMessageService,
  ) {
    // Initialize the form as an empty group.
    this.form = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['availableFilters'] &&
      changes['availableFilters'].currentValue &&
      changes['availableFilters'].currentValue.length > 0
    ) {
      this.applyInitVal();
    }
  }

  applyInitVal() {
    this.form = this.fb.group({});
    this.filterStates = {};

    this.availableFilters.forEach((filter) => {
      this.form.addControl(filter.id.toString(), new FormControl(null));
      if (filter.type === 1 || filter.type === 6) {
        this.initializeFilterState(filter.id);
      }
    });

    if (this.initFilters && this.initFilters.length > 0) {
      this.initFilters.forEach((item) => {
        const control = this.form.get(item.filterId.toString());
        if (control) {
          let initValue = item.value;
          const matchingFilter = this.availableFilters.find((f) => f.id === item.filterId);

          if (matchingFilter) {
            // Handle date range initialization
            if (matchingFilter.type === 2) {
              // Date (range)
              if (typeof initValue === 'string') {
                // Handle both single date and comma-separated dates
                const dates = initValue.split(',');
                const parsedDates = dates
                  .map((d) => {
                    const date = new Date(d);
                    return isNaN(date.getTime()) ? null : date;
                  })
                  .filter((d) => d !== null);

                if (parsedDates.length === 1) {
                  initValue = [parsedDates[0], parsedDates[0]];
                } else if (parsedDates.length === 2) {
                  initValue = parsedDates;
                } else {
                  initValue = null;
                  console.error('Invalid initial date format');
                }
              } else if (Array.isArray(initValue)) {
                // Convert string array to Date array
                initValue = initValue
                  .map((d) => {
                    const date = new Date(d);
                    return isNaN(date.getTime()) ? null : date;
                  })
                  .filter((d) => d !== null);

                if (initValue.length !== 2) {
                  initValue = null;
                  console.error('Invalid date range format');
                }
              } else {
                initValue = null;
              }
            }
            // Handle multi-select initialization
            else if (matchingFilter.type === 6) {
              if (typeof initValue === 'string') {
                initValue = initValue.split(',').map((val) => val.trim());
              } else if (!Array.isArray(initValue)) {
                initValue = [initValue];
              }
            }
          }

          if (initValue !== null) {
            control.setValue(initValue);
          }

          if (matchingFilter && (matchingFilter.type === 1 || matchingFilter.type === 6)) {
            this.loadOptions(item.filterId, '', true);
          }
        }
      });
    }

    setTimeout(() => {
      this.showAfterValueChange = true;
    }, 1000);
  }

  private initializeFilterState(filterId: number): void {
    // Only initialize if not already set.
    if (!this.filterStates[filterId]) {
      const searchSubject = new Subject<string>();
      this.filterStates[filterId] = {
        options: [],
        page: 1,
        pageSize: 500,
        search: '',
        hasMore: true,
        loading: false,
        searchSubject: searchSubject,
      };

      // Debounced search for the dropdown options.
      searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((search) => {
        this.loadOptions(filterId, search, true);
      });
    }
  }

  loadOptions(filterId: number, search: string = '', reset: boolean = false): void {
    const state = this.filterStates[filterId];
    if (!state || state.loading || (!reset && !state.hasMore)) {
      return;
    }

    if (reset) {
      state.page = 1;
      state.hasMore = true;
      state.options = [];
    }

    state.loading = true;
    state.search = search;

    this.dynamicService
      .getFilterDataForDropdown(filterId, state.search, state.page, state.pageSize)
      .subscribe({
        next: (response) => {
          if (!response?.values) {
            throw new Error('Invalid response format');
          }

          state.options = reset ? response.values : [...state.options, ...response.values];
          state.hasMore = response.values.length >= state.pageSize;
          state.page++;
          state.loading = false;
        },
        error: (err) => {
          this.message.error('Failed to load options');
          state.loading = false;
          state.hasMore = false;
        },
      });
  }

  onSearch(filterId: number, search: string): void {
    const state = this.filterStates[filterId];
    if (state?.searchSubject) {
      state.searchSubject.next(search);
    }
  }

  onScrollToBottom(filterId: number): void {
    const state = this.filterStates[filterId];
    if (state && !state.loading && state.hasMore) {
      this.loadOptions(filterId);
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      const result = this.availableFilters
        .filter((filter) => {
          const value = this.form.get(filter.id.toString())?.value;
          return (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !(Array.isArray(value) && value.length === 0)
          );
        })
        .map((filter) => {
          let value = this.form.get(filter.id.toString())?.value;

          // Handle date range conversion
          if (filter.type === 2) {
            if (Array.isArray(value) && value.length === 2) {
              const [start, end] = value;
              // Convert to ISO strings
              const startISO = start.toISOString();
              const endISO = end.toISOString();
              // Join with comma if different, else single string
              value = startISO === endISO ? startISO : `${startISO},${endISO}`;
            } else {
              value = null; // Invalid value
            }
          }
          // Handle multi-select conversion
          else if (filter.type === 6 && Array.isArray(value)) {
            value = value.join(',');
          }

          return {
            filterId: filter.id,
            value: value,
          };
        })
        .filter((item) => item.value !== null);

      this.filterSubmitted.emit(result);
    }
  }

  resetFilters(): void {
    this.form.reset();
    this.filterSubmitted.emit([]);
  }

  ngOnDestroy(): void {
    // Clean up all search subjects.
    Object.values(this.filterStates).forEach((state) => {
      if (state.searchSubject) {
        state.searchSubject.complete();
      }
    });
  }
}
